import React, { useState, useMemo } from "react";
import { HELP } from "../constants/helpContent";
import {
  BarChart3,
  Users,
  Briefcase,
  Tag,
  Heart,
  Download,
  Palette,
  Layers,
  DollarSign,
  FileSearch,
  Trophy,
  RefreshCw,
  Camera,
  TrendingUp,
  MessageSquare,
  Notebook,
  Repeat,
  Award,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, APP_NAME, ICON_SIZES } from "../constants/appConfig";

import { ViewLayout } from "../components/ui/ViewLayout";
import { Card } from "../components/ui/Card";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { TabBar } from "../components/ui/TabBar";
import { GivingReport } from "../components/reports/GivingReport";
import { ReconciliationReport } from "../components/reports/ReconciliationReport";
import { DonorRankingReport } from "../components/reports/DonorRankingReport";
import { RetentionReport } from "../components/reports/RetentionReport";
import { DonorSnapshotReport } from "../components/reports/DonorSnapshotReport";
import { RevenueForecastReport } from "../components/reports/RevenueForecastReport";
import { ContactActivityReport } from "../components/reports/ContactActivityReport";
import { ContactReportsReport } from "../components/reports/ContactReportsReport";
import { MonthlyGiversReport } from "../components/reports/MonthlyGiversReport";
import { DonorRecognitionReport } from "../components/reports/DonorRecognitionReport";

// ─── Tab definitions ───
// Built dynamically inside the component so the Contact Reports tab
// can be gated by canFileContactReports (admin/director only).
const REPORT_TABS_BASE = [
  { id: "giving", label: "Giving", icon: DollarSign },
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "pathways", label: "Pathways", icon: Tag },
  { id: "portfolios", label: "Portfolios", icon: Briefcase },
  { id: "donors", label: "Donors", icon: Heart },
  { id: "snapshot", label: "Snapshot", icon: Camera },
  { id: "forecast", label: "Forecast", icon: TrendingUp },
  { id: "rankings", label: "Rankings", icon: Trophy },
  { id: "recognition", label: "Recognition", icon: Award },
  { id: "retention", label: "Retention", icon: RefreshCw },
  { id: "monthly_givers", label: "Monthly Givers", icon: Repeat },
  { id: "reconciliation", label: "Reconcile", icon: FileSearch },
  { id: "activity", label: "Activity", icon: MessageSquare },
];

// ─── Reusable stat card ───
const StatCard = ({ label, value, sub, color, palette }) => (
  <Card style={{ textAlign: "center", padding: 20 }}>
    <div
      style={{
        fontSize: "1.8rem",
        fontWeight: 700,
        color: color || palette.accent1,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: FONT_SIZES.sm,
        fontWeight: 600,
        color: palette.text1,
        marginTop: 6,
      }}
    >
      {label}
    </div>
    {sub && (
      <div
        style={{ fontSize: FONT_SIZES.xs, color: palette.text3, marginTop: 2 }}
      >
        {sub}
      </div>
    )}
  </Card>
);

// ─── Horizontal bar chart (pure CSS) ───
const BarChart = ({ data, palette }) => {
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d) => (
        <div
          key={d.label}
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          <div
            style={{
              width: 120,
              fontSize: FONT_SIZES.sm,
              color: palette.text1,
              fontWeight: 500,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {d.label}
          </div>
          <div
            style={{
              flex: 1,
              height: 28,
              borderRadius: 6,
              background: `${palette.accent1}08`,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 6,
                background: d.color || palette.accent1,
                width: `${(d.value / maxVal) * 100}%`,
                transition: "width 0.4s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 8,
              }}
            >
              {d.value > 0 && (
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 700,
                    color: "#fff",
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}
                >
                  {d.value}
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              width: 36,
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              textAlign: "right",
            }}
          >
            {maxVal > 0
              ? Math.round(
                  (d.value / data.reduce((s, x) => s + x.value, 0)) * 100
                )
              : 0}
            %
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Data table with optional CSV export ───
const DataTable = ({ columns, rows, palette, exportName }) => {
  const handleExport = () => {
    const header = columns.map((c) => c.label).join(",");
    const csvRows = rows.map((row) =>
      columns
        .map((c) => {
          const val =
            typeof c.accessor === "function"
              ? c.accessor(row)
              : row[c.accessor];
          const str = String(val || "").replace(/"/g, '""');
          return `"${str}"`;
        })
        .join(",")
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportName || "export"}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {exportName && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <button
            onClick={handleExport}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              border: `1px solid ${palette.accent1}`,
              background: `${palette.accent1}08`,
              color: palette.accent1,
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            <Download size={ICON_SIZES.sm} /> Export CSV
          </button>
        </div>
      )}
      <div
        style={{
          overflowX: "auto",
          borderRadius: 8,
          border: `1px solid ${palette.cardBorder}`,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
          }}
        >
          <thead>
            <tr style={{ background: `${palette.accent1}08` }}>
              {columns.map((c) => (
                <th
                  key={c.label}
                  style={{
                    padding: "10px 14px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: palette.text1,
                    borderBottom: `1px solid ${palette.cardBorder}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  background:
                    i % 2 === 0 ? "transparent" : `${palette.accent1}03`,
                }}
              >
                {columns.map((c) => {
                  const val =
                    typeof c.accessor === "function"
                      ? c.accessor(row)
                      : row[c.accessor];
                  return (
                    <td
                      key={c.label}
                      style={{
                        padding: "8px 14px",
                        color: palette.text1,
                        borderBottom: `1px solid ${palette.cardBorder}`,
                        whiteSpace: c.nowrap ? "nowrap" : "normal",
                      }}
                    >
                      {val || "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: palette.text3,
                  }}
                >
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Section header ───
const SectionHeader = ({ icon: SI, title, palette }) => (
  <h3
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: "0 0 16px",
      fontSize: FONT_SIZES.md,
      fontWeight: 600,
      color: palette.text1,
      paddingBottom: 12,
      borderBottom: `1px solid ${palette.cardBorder}`,
    }}
  >
    <SI size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
    {title}
  </h3>
);

// ═══════════════════════════════════════════════════════════════
// REPORTS VIEW
// ═══════════════════════════════════════════════════════════════
export const ReportsView = () => {
  const {
    palette,
    toolbarStyle,
    members,
    directors,
    pathways,
    goBack,
    goHome,
    lastReportTab,
    setLastReportTab,
    canFileContactReports,
  } = useApp();
  const [activeTab, setActiveTab] = useState(lastReportTab || "giving");

  // Build the tab list dynamically — Contact Reports is admin/director only
  const reportTabs = useMemo(() => {
    const tabs = [...REPORT_TABS_BASE];
    if (canFileContactReports) {
      tabs.push({ id: "contactReports", label: "Contact Reports", icon: Notebook });
    }
    return tabs;
  }, [canFileContactReports]);

  // Persist tab selection so it survives navigation away and back
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setLastReportTab(tab);
  };

  // ─── Computed report data ───
  const stats = useMemo(() => {
    const total = members.length;
    const donors = members.filter((m) => m.isDonor).length;
    const board = members.filter((m) => m.isBoardMember).length;
    const assigned = members.filter((m) => m.assignedDirector?.trim()).length;
    const unassigned = total - assigned;
    const withEmail = members.filter((m) => m.email?.trim()).length;
    const withOrg = members.filter((m) => m.organization?.trim()).length;

    // Pathway distribution
    const pathwayData = pathways.map((pw) => ({
      label: pw.name,
      value: members.filter((m) => m.pathway === pw.id).length,
      color: pw.color,
    }));

    // Donor type breakdown
    const individuals = members.filter((m) => !m.donorType || m.donorType === "individual").length;
    const households = members.filter((m) => m.donorType === "household").length;
    const organizations = members.filter((m) => m.donorType === "organization").length;
    const donorTypeData = [
      { label: "Individual", value: individuals, color: "#3b82f6" },
      { label: "Household", value: households, color: "#8b5cf6" },
      { label: "Organization", value: organizations, color: "#f59e0b" },
    ];

    // Org-pays-dues
    const orgPays = members.filter((m) => {
      const v = (m.orgPaysDues || "").toLowerCase();
      return v === "yes" || v === "true" || v === "1";
    }).length;

    // Portfolio workload
    const portfolioData = directors
      .map((bm) => {
        const name = `${bm.firstName} ${bm.lastName}`;
        const assigned = members.filter(
          (m) => m.assignedDirector === name
        ).length;
        return { director: name, assigned, email: bm.email || "" };
      })
      .sort((a, b) => b.assigned - a.assigned);

    const unassignedMembers = members.filter((m) => !m.assignedDirector?.trim());

    return {
      total,
      donors,
      board,
      assigned,
      unassigned,
      withEmail,
      withOrg,
      orgPays,
      pathwayData,
      donorTypeData,
      individuals,
      households,
      organizations,
      portfolioData,
      unassignedMembers,
    };
  }, [members, directors, pathways]);

  // Donor roster
  const donorMembers = useMemo(
    () =>
      members
        .filter((m) => m.isDonor)
        .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || "")),
    [members]
  );


  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Reports & Analytics"
        subtitle={`${members.length} donors • ${pathways.length} pathways`}
        icon={BarChart3}
        helpContent={HELP.reports}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TabBar
          tabs={reportTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Content Area */}
        <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            {/* ═══ GIVING ═══ */}
            {activeTab === "giving" && <GivingReport />}

            {/* ═══ DASHBOARD ═══ */}
            {activeTab === "dashboard" && (
              <>
                {/* KPI Cards */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 12,
                    marginBottom: 24,
                  }}
                >
                  <StatCard
                    label="Total Donors"
                    value={stats.total}
                    palette={palette}
                  />
                  <StatCard
                    label="Donors"
                    value={stats.donors}
                    color="#10b981"
                    palette={palette}
                  />
                  <StatCard
                    label="Board Donors"
                    value={stats.board}
                    color="#8b5cf6"
                    palette={palette}
                  />
                  <StatCard
                    label="Assigned"
                    value={stats.assigned}
                    color="#3b82f6"
                    palette={palette}
                  />
                  <StatCard
                    label="Unassigned"
                    value={stats.unassigned}
                    color={stats.unassigned > 0 ? "#ef4444" : "#9ca3af"}
                    palette={palette}
                  />
                  <StatCard
                    label="Households"
                    value={stats.households}
                    color="#8b5cf6"
                    palette={palette}
                  />
                  <StatCard
                    label="Organizations"
                    value={stats.organizations}
                    color="#f59e0b"
                    palette={palette}
                  />
                </div>

                {/* Pathway Distribution Chart */}
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon={Tag}
                    title="Pathway Distribution"
                    palette={palette}
                  />
                  <BarChart data={stats.pathwayData} palette={palette} />
                </Card>

                {/* Donor Type Distribution */}
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon={Users}
                    title="Donor Type Breakdown"
                    palette={palette}
                  />
                  <BarChart data={stats.donorTypeData} palette={palette} />
                </Card>

                {/* Quick Stats */}
                <Card>
                  <SectionHeader
                    icon={Layers}
                    title="Data Quality"
                    palette={palette}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      fontSize: FONT_SIZES.sm,
                    }}
                  >
                    <div>
                      <span style={{ color: palette.text2 }}>Have Email:</span>{" "}
                      <span style={{ color: palette.text1, fontWeight: 600 }}>
                        {stats.withEmail} (
                        {stats.total
                          ? Math.round((stats.withEmail / stats.total) * 100)
                          : 0}
                        %)
                      </span>
                    </div>
                    <div>
                      <span style={{ color: palette.text2 }}>Have Org:</span>{" "}
                      <span style={{ color: palette.text1, fontWeight: 600 }}>
                        {stats.withOrg} (
                        {stats.total
                          ? Math.round((stats.withOrg / stats.total) * 100)
                          : 0}
                        %)
                      </span>
                    </div>
                    <div>
                      <span style={{ color: palette.text2 }}>
                        Org Pays Dues:
                      </span>{" "}
                      <span style={{ color: palette.text1, fontWeight: 600 }}>
                        {stats.orgPays}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: palette.text2 }}>
                        Board Coverage:
                      </span>{" "}
                      <span style={{ color: palette.text1, fontWeight: 600 }}>
                        {stats.total
                          ? Math.round((stats.assigned / stats.total) * 100)
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* ═══ PATHWAY DISTRIBUTION ═══ */}
            {activeTab === "pathways" && (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon={Tag}
                    title="Pathway Distribution"
                    palette={palette}
                  />
                  <BarChart data={stats.pathwayData} palette={palette} />
                </Card>

                <Card>
                  <SectionHeader
                    icon={Users}
                    title="Donors by Pathway"
                    palette={palette}
                  />
                  {pathways.map((pw) => {
                    const pwMembers = members
                      .filter((m) => m.pathway === pw.id)
                      .sort((a, b) =>
                        (a.lastName || "").localeCompare(b.lastName || "")
                      );
                    return (
                      <div key={pw.id} style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              background: pw.color,
                            }}
                          />
                          <span
                            style={{
                              fontSize: FONT_SIZES.sm,
                              fontWeight: 600,
                              color: palette.text1,
                            }}
                          >
                            {pw.name}
                          </span>
                          <span
                            style={{
                              fontSize: FONT_SIZES.xs,
                              color: palette.text3,
                            }}
                          >
                            ({pwMembers.length})
                          </span>
                        </div>
                        {pwMembers.length > 0 ? (
                          <DataTable
                            columns={[
                              {
                                label: "Name",
                                accessor: (m) =>
                                  `${m.lastName}, ${m.firstName}`,
                                nowrap: true,
                              },
                              {
                                label: "Organization",
                                accessor: "organization",
                              },
                              { label: "Email", accessor: "email" },
                              { label: "Director", accessor: "assignedDirector" },
                            ]}
                            rows={pwMembers}
                            palette={palette}
                          />
                        ) : (
                          <div
                            style={{
                              padding: 12,
                              fontSize: FONT_SIZES.xs,
                              color: palette.text3,
                              fontStyle: "italic",
                            }}
                          >
                            No donors in this pathway
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Card>
              </>
            )}

            {/* ═══ BOARD PORTFOLIOS ═══ */}
            {activeTab === "portfolios" && (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon={Briefcase}
                    title="Portfolio Workload"
                    palette={palette}
                  />
                  <BarChart
                    data={stats.portfolioData.map((p) => ({
                      label: p.director,
                      value: p.assigned,
                      color: palette.accent1,
                    }))}
                    palette={palette}
                  />
                </Card>

                {stats.portfolioData.map((p) => {
                  const assigned = members
                    .filter((m) => m.assignedDirector === p.director)
                    .sort((a, b) =>
                      (a.lastName || "").localeCompare(b.lastName || "")
                    );
                  return (
                    <Card key={p.director} style={{ marginBottom: 16 }}>
                      <SectionHeader
                        icon={Users}
                        title={`${p.director} (${assigned.length})`}
                        palette={palette}
                      />
                      <DataTable
                        columns={[
                          {
                            label: "Name",
                            accessor: (m) => `${m.lastName}, ${m.firstName}`,
                            nowrap: true,
                          },
                          {
                            label: "Pathway",
                            accessor: (m) => {
                              const pw = pathways.find(
                                (x) => x.id === m.pathway
                              );
                              return pw?.name || m.pathway;
                            },
                          },
                          { label: "Organization", accessor: "organization" },
                          { label: "Email", accessor: "email" },
                          {
                            label: "Donor",
                            accessor: (m) => (m.isDonor ? "✓" : ""),
                          },
                        ]}
                        rows={assigned}
                        palette={palette}
                        exportName={`Portfolio_${p.director.replace(
                          /\s+/g,
                          "_"
                        )}`}
                      />
                    </Card>
                  );
                })}

                {stats.unassignedMembers.length > 0 && (
                  <Card>
                    <SectionHeader
                      icon={Users}
                      title={`Unassigned (${stats.unassignedMembers.length})`}
                      palette={palette}
                    />
                    <DataTable
                      columns={[
                        {
                          label: "Name",
                          accessor: (m) => `${m.lastName}, ${m.firstName}`,
                          nowrap: true,
                        },
                        {
                          label: "Pathway",
                          accessor: (m) => {
                            const pw = pathways.find((x) => x.id === m.pathway);
                            return pw?.name || m.pathway;
                          },
                        },
                        { label: "Organization", accessor: "organization" },
                        { label: "Email", accessor: "email" },
                      ]}
                      rows={stats.unassignedMembers.sort((a, b) =>
                        (a.lastName || "").localeCompare(b.lastName || "")
                      )}
                      palette={palette}
                      exportName="Unassigned_Donors"
                    />
                  </Card>
                )}
              </>
            )}

            {/* ═══ DONOR ROSTER ═══ */}
            {activeTab === "donors" && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <StatCard
                    label="Total Donors"
                    value={stats.donors}
                    color="#10b981"
                    palette={palette}
                  />
                  <StatCard
                    label="Donor Rate"
                    value={`${
                      stats.total
                        ? Math.round((stats.donors / stats.total) * 100)
                        : 0
                    }%`}
                    sub="of all donors"
                    palette={palette}
                  />
                  <StatCard
                    label="Board + Donor"
                    value={
                      members.filter((m) => m.isDonor && m.isBoardMember).length
                    }
                    color="#8b5cf6"
                    palette={palette}
                  />
                </div>

                <Card>
                  <SectionHeader
                    icon={Heart}
                    title={`Donor Roster (${donorMembers.length})`}
                    palette={palette}
                  />
                  <DataTable
                    columns={[
                      {
                        label: "Last Name",
                        accessor: "lastName",
                        nowrap: true,
                      },
                      {
                        label: "First Name",
                        accessor: "firstName",
                        nowrap: true,
                      },
                      { label: "Organization", accessor: "organization" },
                      { label: "Email", accessor: "email" },
                      {
                        label: "Pathway",
                        accessor: (m) => {
                          const pw = pathways.find((x) => x.id === m.pathway);
                          return pw?.name || m.pathway;
                        },
                      },
                      { label: "Director", accessor: "assignedDirector" },
                      {
                        label: "Board",
                        accessor: (m) => (m.isBoardMember ? "✓" : ""),
                      },
                    ]}
                    rows={donorMembers}
                    palette={palette}
                    exportName="Donor_Roster"
                  />
                </Card>
              </>
            )}

            {activeTab === "snapshot" && <DonorSnapshotReport />}

            {activeTab === "forecast" && <RevenueForecastReport />}

            {activeTab === "rankings" && <DonorRankingReport />}

            {activeTab === "recognition" && <DonorRecognitionReport />}

            {activeTab === "retention" && <RetentionReport />}

            {activeTab === "monthly_givers" && <MonthlyGiversReport />}

            {activeTab === "reconciliation" && <ReconciliationReport />}

            {activeTab === "activity" && <ContactActivityReport />}

            {activeTab === "contactReports" && canFileContactReports && (
              <ContactReportsReport />
            )}
          </div>
        </div>
      </div>

      <ZoneThreeFooter />
    </ViewLayout>
  );
};
